import { router } from 'expo-router';
import { Platform } from 'react-native';

import { routes } from '@/constants/routes';

// Native standby is a screen inside the installed app (pushed from the entries
// list / home drawer), so leaving or exiting returns the client there — never
// the public join flow (SQR-8979). Web/QR IS that flow, so it resets to the flow
// start (and signs out — owned by each screen). Session-gated so a signed-out
// native edge keeps the public fallback.
export const isStandbyInAppClient = (customerId?: string): boolean =>
  Platform.OS !== 'web' && !!customerId;

// Back to where the client came from (the entries list redirects Home at 0
// entries), or Home when a cold deep link left no back stack.
export const returnToStandbyApp = (): void => {
  router.canGoBack() ? router.back() : router.replace(routes.home);
};

// Leaving or a terminal entry state: native stays in the app, web/QR resets to
// the public flow start.
export const leaveStandbyTerminal = (
  shopId: string,
  customerId?: string,
): void => {
  isStandbyInAppClient(customerId)
    ? returnToStandbyApp()
    : router.replace(routes.standby(shopId));
};

// A native exit whose leave emptied the list: pop the WHOLE standby stack back
// to Home so the client never lands on an empty list (SQR-8979). `dismissAll`
// unwinds the pushed standby screens; a cold deep link with nothing to dismiss
// falls back to `replace(home)`. Not `dismissTo(home)`: to a route that isn't in
// this non-modal nested stack's history it only pops one level and strands the
// client on the just-left list (QA r2; see ChooseTimeScreen / SQR-15312).
export const returnToStandbyHome = (): void => {
  router.canDismiss() ? router.dismissAll() : router.replace(routes.home);
};

// A native exit with entries still on the list: return to it (the caller has
// just refetched the shared query, so the left/booked entry is gone). When the
// client came from the entries list it sits under the entry, so `back()` returns
// to that refreshed instance rather than pushing a duplicate list; a cold deep
// link with nothing under it falls back to the list route. Web/QR exits are the
// public-flow reset owned by the caller.
export const returnToStandbyEntries = (): void => {
  router.canGoBack() ? router.back() : router.replace(routes.standbyEntries);
};

// A native flow exit returns to the app without signing out; returns true when it
// handled navigation. Web / no-session callers get false and keep their sign-out.
export const exitStandbyToAppIfNative = (customerId?: string): boolean => {
  if (!isStandbyInAppClient(customerId)) return false;
  returnToStandbyApp();
  return true;
};
